import { FC, useContext, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useMatch, useNavigate } from "react-router-dom";

import { StepGuard } from "@/components/Checkout/StepGuard/StepGuard";
import { Button } from "@/components/Interface/Button/Button";
import { StepMenuItem } from "@/components/Interface/Stepper/StepMenu";
import { StepFlowLayout } from "@/components/Layout/BaseLayouts/StepFlowLayout/StepFlowLayout";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import {
  ADD_DEVICE_TOTAL_STEPS,
  addDeviceStepMapping,
  addDeviceStepPaths,
} from "@/components/Portal/AddDevice/addDevice.constants";
import { AddDeviceBlockedModal } from "@/components/Portal/AddDevice/AddDeviceBlockedModal/AddDeviceBlockedModal";
import { AddDeviceOverflowMenu } from "@/components/Portal/AddDevice/AddDeviceOverflowMenu/AddDeviceOverflowMenu";
import { DataContext } from "@/provider/DataContextProvider";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { useGetSimCustomerActions } from "@/services/api";
import {
  getAddDeviceBlockedReasons,
  isAddDeviceFullyBlocked,
} from "@/utils/customerUtils";
import { getEntry } from "@/utils/dataContextHelpers";
import { useHasPrechargedMobileAbo } from "@/utils/tariffUtils";
import {
  getAddDeviceStepNumbering,
  getAddDeviceSteps,
  resolveWizardOptionId,
} from "@/utils/wizardNavigation";

type AddDeviceLocationState = {
  /** Set by flows that already prepared a basket (e.g. Flat Mobile upsell). */
  keepBasket?: boolean;
};

export const AddDevice: FC = () => {
  const { t } = useTranslation("portal", { keyPrefix: "add-device" });
  const { t: tCommon } = useTranslation();
  const { dataContext, setDataContext } = useContext(DataContext);
  const location = useLocation();
  const navigate = useNavigate();
  const routeMatch = useMatch(`${portalRoutes.addDevice.base}/:step/:substep`);
  // Capture once on mount — later in-wizard navigations drop location.state and
  // must not re-trigger a basket wipe (that bounces the user back to step 1).
  const keepBasketOnEntry = useRef(
    !!(location.state as AddDeviceLocationState | null)?.keepBasket,
  );
  const [isReady, setIsReady] = useState(keepBasketOnEntry.current);

  const { data: simCustomerInfo, isLoading: isLoadingSimActions } =
    useGetSimCustomerActions();
  const isBlocked = isAddDeviceFullyBlocked(simCustomerInfo);
  const blockedReasons = getAddDeviceBlockedReasons(simCustomerInfo);

  // Once the tariff is known (Number porting / Checkout), the stepper should
  // reflect only the steps this specific flow actually goes through - a
  // precharged, Flat Home, or purchase-intent tariff skips some of them.
  const hasPrechargedMobileAbo = useHasPrechargedMobileAbo(dataContext.basket);
  const entry = getEntry(dataContext);
  const optionId = resolveWizardOptionId({
    basketOptionId: entry?.options?.[0]?.product?.id,
    selectedOptionId: dataContext.selectedOption?.id,
    wizardEntryOptionId: dataContext.wizardEntry?.optionId,
  });
  const isPrecharged =
    !!dataContext.selectedOption?.precharged || hasPrechargedMobileAbo;

  // Reset step progress only when opening Add Device fresh from the cockpit.
  // Must not run on checkout refresh, Datatrans return, or any other remount
  // while the user is already mid-flow — that would drop allowedMax to 1 and
  // StepGuard would bounce them back to sim-selection.
  useLayoutEffect(() => {
    if (keepBasketOnEntry.current) {
      setIsReady(true);

      return;
    }

    const currentStepNumber = routeMatch?.params.step
      ? Number.parseInt(routeMatch.params.step, 10)
      : 1;

    setDataContext((prev) => {
      // Completed onboarding — undefined means "no step lock" for StepGuard.
      if (prev.allowedMaxOnboardingStep === undefined) {
        return prev;
      }

      const hasInProgressFlow =
        currentStepNumber > 1 ||
        (prev.basket?.id !== undefined &&
          (prev.allowedMaxOnboardingStep ?? 0) > 1);

      if (hasInProgressFlow) {
        return prev;
      }

      return {
        allowedMaxOnboardingStep: 1,
        isEmailVerified: prev.isEmailVerified ?? false,
      };
    });
    setIsReady(true);
  }, [routeMatch?.params.step, setDataContext]);

  const handleCancel = () => {
    navigate(portalRoutes.cockpit.base);
  };

  const handleBasketDeleted = () => {
    setDataContext((prev) => ({
      allowedMaxOnboardingStep: undefined,
      isEmailVerified: prev.isEmailVerified,
    }));
  };

  const handleCloseBlockedModal = () => {
    navigate(portalRoutes.cockpit.base, { replace: true });
  };

  if (!isReady || isLoadingSimActions) {
    return <LoadingSpinner />;
  }

  if (isBlocked) {
    return (
      <AddDeviceBlockedModal
        reasons={blockedReasons}
        onClose={handleCloseBlockedModal}
      />
    );
  }

  const substep = routeMatch?.params.substep ?? "sim-selection";
  const currentStep =
    addDeviceStepMapping[substep] ?? addDeviceStepMapping["sim-selection"];
  const { step, totalSteps } = getAddDeviceStepNumbering({
    substep,
    optionId,
    isPrecharged,
    wizardEntry: dataContext.wizardEntry,
    fallbackTotalSteps: ADD_DEVICE_TOTAL_STEPS,
  });

  const menuItems: StepMenuItem[] = getAddDeviceSteps({
    optionId,
    isPrecharged,
    wizardEntry: dataContext.wizardEntry,
  }).map((stepSubstep) => {
    const stepMeta = addDeviceStepMapping[stepSubstep];

    return {
      // The step keys are looked up dynamically, which a literal-typed `t`
      // cannot express - see the flow's constants file.
      label: t(stepMeta.label as never),
      path: addDeviceStepPaths[stepSubstep],
      disabled:
        dataContext.allowedMaxOnboardingStep !== undefined &&
        dataContext.allowedMaxOnboardingStep <= stepMeta.step,
    };
  });

  return (
    <StepGuard basePath={portalRoutes.addDevice.base}>
      <StepFlowLayout
        dataTestid="add-device"
        title={t("heading")}
        step={step}
        totalSteps={totalSteps}
        // The step keys are looked up dynamically, which a literal-typed `t`
        // cannot express - see the flow's constants file.
        stepLabel={t(currentStep.label as never)}
        description={
          currentStep.description && t(currentStep.description as never)
        }
        menuItems={menuItems}
        menuToggleLabel={t("stepMenuLabel")}
        headerAction={
          <div className="flex w-full items-center justify-between">
            <Button
              dataTestid="add-device-cancel"
              variant="tertiary"
              onClick={handleCancel}
            >
              {tCommon("Common.label.cancel")}
            </Button>
            <AddDeviceOverflowMenu
              basketId={dataContext.basket?.id}
              onBasketDeleted={handleBasketDeleted}
            />
          </div>
        }
      >
        <Outlet context={portalRoutes.addDevice.base} />
      </StepFlowLayout>
    </StepGuard>
  );
};
